import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/toaster';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Register from './components/Register';
import EnhancedRiderAuth from './components/rider/EnhancedRiderAuth';
import RiderDashboard from './components/rider/RiderDashboard';
import YachanguStyleDashboard from './components/rider/YachanguStyleDashboard';
import PawaPayTestPage from './components/rider/PawaPayTestPage';
import { CountryProvider } from './contexts/CountryContext';
import EnhancedDriverAuth from './components/driver/EnhancedDriverAuth';
import DriverDashboard from './components/driver/v2/DriverDashboardV2';
import ProtectedRoute from './components/ProtectedRoute';
import { AdminProvider } from './contexts/AdminContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import UserDetails from './pages/admin/UserDetails';
import FinancialPage from './pages/admin/FinancialPage';
import PricingConfig from './pages/admin/PricingConfig';
import DisbursementsPage from './pages/admin/DisbursementsPage';
import AdminSettings from './pages/admin/AdminSettings';
import ReportsPage from './pages/admin/ReportsPage';
import PromotionsPage from './pages/admin/PromotionsPage';
import ReferralManagementPage from './pages/admin/ReferralManagementPage';
import { authStorage } from './utils/storage';
import GoogleMapsProvider from './components/maps/GoogleMapsProvider';
import { initializeCapacitorCompatibility } from './utils/capacitorHelper';
import { useTheme, resetThemeToLight } from './hooks/useTheme';

// Wrapper components for route-based navigation
function RiderAuthWrapper({ onAuthSuccess, userData, setUserData, setIsAuthenticated }) {
  const navigate = useNavigate();
  
  const handleAuthSuccess = (user) => {
    setUserData(user);
    setIsAuthenticated(true);
    onAuthSuccess(user);
    navigate('/rider/dashboard');
  };

  const handleBack = () => {
    navigate('/');
  };

  return <EnhancedRiderAuth onBack={handleBack} onAuthSuccess={handleAuthSuccess} />;
}

function DriverAuthWrapper({ onAuthSuccess, userData, setUserData, setIsAuthenticated }) {
  const navigate = useNavigate();
  
  const handleAuthSuccess = (user) => {
    setUserData(user);
    setIsAuthenticated(true);
    onAuthSuccess(user);
    navigate('/driver/dashboard');
  };

  const handleBack = () => {
    navigate('/');
  };

  return <EnhancedDriverAuth onBack={handleBack} onAuthSuccess={handleAuthSuccess} />;
}

function RiderDashboardWrapper({ userData, setUserData, setIsAuthenticated }) {
  const navigate = useNavigate();

  const handleUpdateUser = (updatedUser) => {
    setUserData(updatedUser);
  };

  const handleLogout = async () => {
    // Clear auth storage (works for both web and mobile)
    await authStorage.clearAuth();
    
    // Reset theme so the next user lands on a clean slate (their own theme will load on login)
    resetThemeToLight();
    
    // Update state
    setUserData(null);
    setIsAuthenticated(false);
    
    // Navigate after state updates
    setTimeout(() => {
      navigate('/');
    }, 0);
  };

  return <YachanguStyleDashboard user={userData} onUpdateUser={handleUpdateUser} onLogout={handleLogout} />;
}

function DriverDashboardWrapper({ userData, setUserData, setIsAuthenticated }) {
  const navigate = useNavigate();

  const handleUpdateUser = (updatedUser) => {
    setUserData(updatedUser);
  };

  const handleLogout = async () => {
    // Clear auth storage (works for both web and mobile)
    await authStorage.clearAuth();
    
    // Reset theme so the next user lands on a clean slate
    resetThemeToLight();
    
    // Update state
    setUserData(null);
    setIsAuthenticated(false);
    
    // Navigate after state updates
    setTimeout(() => {
      navigate('/');
    }, 0);
  };

  return <DriverDashboard user={userData} onUpdateUser={handleUpdateUser} onLogout={handleLogout} />;
}

function App() {
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize theme on app load — scoped to current user account
  useTheme(userData);

  // Initialize Capacitor compatibility on mount
  useEffect(() => {
    initializeCapacitorCompatibility();
  }, []);

  // Load user data from storage on mount (works for both web and mobile)
  useEffect(() => {
    const loadAuthData = async () => {
      try {
        const authData = await authStorage.getAuth();
        
        if (authData && authData.token && authData.user) {
          // Sessions always persist — users stay signed in until they log out
          // from Settings (which calls authStorage.clearAuth).
          setUserData(authData.user);
          setIsAuthenticated(true);
          console.log('✅ User auto-logged in from saved session');
        }
      } catch (error) {
        console.error('Failed to load auth data:', error);
        await authStorage.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthData();
  }, []);

  const handleAuthSuccess = (user) => {
    setUserData(user);
    setIsAuthenticated(true);
  };

  return (
    <CountryProvider>
    <GoogleMapsProvider>
      <Router>
        <div className="App">
          {isLoading ? (
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            </div>
          ) : (
          <Routes>
          {/* Landing / Home — launch straight into the redesigned Login */}
          <Route
            path="/"
            element={
              isAuthenticated && userData
                ? <Navigate to={userData.role === 'driver' ? '/driver/dashboard' : '/rider/dashboard'} replace />
                : <Login onAuthSuccess={handleAuthSuccess} setUserData={setUserData} setIsAuthenticated={setIsAuthenticated} />
            }
          />

          {/* Auth Routes */}
          <Route 
            path="/login" 
            element={
              <Login 
                onAuthSuccess={handleAuthSuccess}
                setUserData={setUserData}
                setIsAuthenticated={setIsAuthenticated}
              />
            } 
          />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/sandbox-pay" element={<PawaPayTestPage />} />

          {/* Rider Routes */}
          <Route 
            path="/rider/auth" 
            element={
              <RiderAuthWrapper 
                onAuthSuccess={handleAuthSuccess}
                userData={userData}
                setUserData={setUserData}
                setIsAuthenticated={setIsAuthenticated}
              />
            } 
          />
          <Route 
            path="/rider/dashboard" 
            element={
              <ProtectedRoute role="rider" isAuthenticated={isAuthenticated} userData={userData}>
                <RiderDashboardWrapper userData={userData} setUserData={setUserData} setIsAuthenticated={setIsAuthenticated} />
              </ProtectedRoute>
            } 
          />

          {/* Driver Routes */}
          <Route 
            path="/driver/auth" 
            element={
              <DriverAuthWrapper 
                onAuthSuccess={handleAuthSuccess}
                userData={userData}
                setUserData={setUserData}
                setIsAuthenticated={setIsAuthenticated}
              />
            } 
          />
          <Route 
            path="/driver/dashboard" 
            element={
              <ProtectedRoute role="driver" isAuthenticated={isAuthenticated} userData={userData}>
                <DriverDashboardWrapper userData={userData} setUserData={setUserData} setIsAuthenticated={setIsAuthenticated} />
              </ProtectedRoute>
            } 
          />

          {/* Admin Routes */}
          <Route 
            path="/admin/*" 
            element={
              <AdminProvider>
                <Routes>
                  <Route path="login" element={<AdminLogin />} />
                  <Route path="" element={<AdminLayout />}>
                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="users/:userId" element={<UserDetails />} />
                    <Route path="financial" element={<FinancialPage />} />
                    <Route path="pricing" element={<PricingConfig />} />
                    <Route path="disbursements" element={<DisbursementsPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="promotions" element={<PromotionsPage />} />
                    <Route path="referrals" element={<ReferralManagementPage />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>
                </Routes>
              </AdminProvider>
            }
          />

          {/* Fallback - Redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        )}
        <Toaster />
      </div>
    </Router>
    </GoogleMapsProvider>
    </CountryProvider>
  );
}

export default App;